import { toastManager } from "@/lib/toast-manager";
import { useUserProfileStore } from "@/stores/user-profile-store";
import { HttpMethod } from "@/types/https";
import { getWorkchestraOrigin } from "@/lib/utils";
import axios from "axios";

interface ApiCallParams {
  url: string;
  method: string;
  params?: any;
  pathParams?: Record<string, string | number>;
  body?: Record<string, any>;
  headers?: Record<string, string>;
}

const api = axios.create({
  baseURL: `${getWorkchestraOrigin()}/api/pps`,
  paramsSerializer: (params) => {
    if (!params || Object.keys(params).length === 0) {
      return "";
    }

    const serializedParams = Object.entries(params)
      .flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map(
            (val) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`,
          );
        } else if (key === "encodeQuery") {
          const encodeQueryParams = Object.entries(value).map(
            ([encodeKey, encodeValue]) => {
              if (Array.isArray(encodeValue)) {
                return `${encodeKey}=${encodeURIComponent(JSON.stringify(encodeValue))}`;
              }
              return "";
            },
          );
          return encodeQueryParams.filter(Boolean).join("&");
        } else if (value !== null && value !== undefined) {
          return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        }

        return "";
      })
      .filter(Boolean)
      .join("&");

    return serializedParams;
  },
});

export const apiCall = async <T>({
  url,
  params,
  body,
  method,
}: ApiCallParams): Promise<T> => {
  const { token } = useUserProfileStore.getState();
  try {
    const config = {
      url,
      method,
      params,
      ...(method === HttpMethod.POST ||
      method === HttpMethod.PUT ||
      method === HttpMethod.PATCH ||
      method === HttpMethod.DELETE
        ? {
            data: body,
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        : {}),
    };
    const response = await api.request(config);

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (method === HttpMethod.GET && error.response?.status !== 404) {
        const errorMessage =
          error.response?.data?.title ||
          error.response?.data?.message ||
          (typeof error.response?.data === "string"
            ? error.response.data
            : "An error occurred while fetching data.");

        toastManager.showError("Failed to fetch data", errorMessage);
      }
      throw error.response?.data || error;
    }
    throw error;
  }
};
